"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { ArrowRight, BrainCircuit, Check, Sparkles, TestTube2, X, type LucideIcon } from "lucide-react";
import { BrandMark } from "@/components/brand-logo";
import { lockScroll } from "@/lib/scroll-lock";
import type { ComingSoonTopic } from "@/lib/coming-soon";

const ICONS: Record<string, LucideIcon> = {
  cypress: TestTube2,
  "ai-testing": BrainCircuit,
};

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ComingSoonDialogProps {
  topic: ComingSoonTopic;
  onClose: () => void;
}

/** Mounted only while open, so scroll lock and focus handling follow mount/unmount. */
export function ComingSoonDialog({ topic, onClose }: ComingSoonDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLAnchorElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const unlock = lockScroll();
    primaryRef.current?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      // Keep keyboard focus inside the dialog while it is open.
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      unlock();
      previouslyFocused?.focus?.({ preventScroll: true });
    };
  }, []);

  if (typeof document === "undefined") return null;

  const Icon = ICONS[topic.slug] ?? Sparkles;

  return createPortal(
    <div
      className="animate-fade-in fixed inset-0 z-[150] flex items-end justify-center bg-[#13221a]/55 p-3 backdrop-blur-[3px] sm:items-center sm:p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="coming-soon-title"
        aria-describedby="coming-soon-desc"
        className="animate-dialog-in relative w-full max-w-[420px] overflow-hidden rounded-2xl border border-line bg-white shadow-[0_24px_60px_-12px_rgba(19,34,26,0.45)]"
      >
        <div className="flex items-center justify-between border-b border-[#13221a]/10 bg-lime px-5 py-3">
          <div className="flex items-center gap-2">
            <BrandMark size={26} />
            <span className="font-display text-[17px] font-bold leading-none tracking-[-0.035em] text-[#13221a]">
              Target<span className="text-[#0B996E]">QA</span>
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="grid h-8 w-8 place-items-center rounded-full text-[#13221a]/70 transition hover:bg-[#13221a]/10 hover:text-[#13221a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#13221a]/40"
          >
            <X size={17} aria-hidden="true" />
          </button>
        </div>

        <div className="px-6 pb-6 pt-6 text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-[#13221a] text-lime shadow-[0_8px_18px_-6px_rgba(19,34,26,0.5)]">
            <Icon size={22} strokeWidth={2.2} aria-hidden="true" />
          </div>

          <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-line bg-wash px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-green">
            <span className="animate-pulse-ring h-1.5 w-1.5 rounded-full bg-green" aria-hidden="true" />
            Coming soon
          </p>
          <h2 id="coming-soon-title" className="mt-3 font-display text-[22px] font-bold leading-tight tracking-[-0.02em] text-ink">
            {topic.name} practice is on its way
          </h2>
          <p id="coming-soon-desc" className="mx-auto mt-2 max-w-[330px] text-sm leading-relaxed text-muted">
            {topic.summary}
          </p>

          <div className="mt-5 rounded-xl border border-line bg-wash px-4 py-3.5 text-left">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">What to expect</p>
            <ul className="mt-2.5 space-y-2.5">
              {topic.expect.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] leading-snug text-ink">
                  <span className="mt-px grid h-4 w-4 shrink-0 place-items-center rounded-full bg-green text-white">
                    <Check size={10} strokeWidth={3} aria-hidden="true" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-4 text-[13px] leading-snug text-muted">
            Playwright and Selenium practice is open now, so you can keep building your skills today.
          </p>

          <div className="mt-5 flex flex-col gap-2">
            <Link
              ref={primaryRef}
              prefetch={false}
              href="/practice"
              onClick={onClose}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-ink text-sm font-semibold text-white transition hover:bg-[#13221a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2"
            >
              Explore available tracks
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="h-10 w-full rounded-xl text-sm font-medium text-muted transition hover:bg-wash hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
